/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { isOrgsOnFTEnabled } from '@thoughtspot/feature-service';
import { useTranslation } from '@thoughtspot/i18n';
import {
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import { formatDataValue } from '@thoughtspot/number-format-util';
import {
    BannerAlert,
    BannerAlertType,
} from '@thoughtspot/radiant-react/widgets/alert/banner/banner-alert';
import {
    Colors,
    Typography,
} from '@thoughtspot/radiant-react/widgets/typography';
import {
    hasAdminPrivileges,
    setSourceToPricingPage,
} from '@thoughtspot/session-service';
import React, { useEffect, useState } from 'react';
import styles from './user-and-data-usage-banner.module.scss';

export interface UserAndDataUsageProps {
    percent?: number;
    reduceRowDocUrl: string;
    isOwner: boolean;
    collectEvent: (userAction: UserAction, propertyMap: PropertyMap) => void;
    goToTeamsPage: () => void;
    goToAdmin: (tabId?: string) => void;
    numUsersInOrg?: number;
    rowLimit?: number;
    currentUsage?: number;
    memberLimit?: number;
    showUserLimitBanner?: boolean;
    showDataUsageBanner?: boolean;
}

enum BannerType {
    data = 'Data',
    user = 'User',
    userAndData = 'User and data',
}

export const UserAndDataUsageBanner: React.FC<UserAndDataUsageProps> = ({
    percent,
    reduceRowDocUrl,
    isOwner,
    collectEvent,
    goToTeamsPage,
    goToAdmin,
    numUsersInOrg,
    rowLimit,
    currentUsage,
    memberLimit,
    showUserLimitBanner,
    showDataUsageBanner,
}) => {
    const { t } = useTranslation();
    const formattedUsage = formatDataValue(currentUsage, {
        noShorten: false,
    });
    const formattedRowLimit = formatDataValue(rowLimit, {
        noShorten: false,
    });

    const rowLimitUserActionProperties = {
        dataUsageBannerMessage: `${t('data_usage_banner_text', {
            percent,
        })} + ${t('reduce_row_text')} + ${t('upgrade_plan_text')}`,
        dataUsagePercentage: percent,
        dataUsageOwner: isOwner === true,
        dataUsageBannerColour: 'blue',
    };

    const userLimitUserActionProperties = {
        userUsageBannerMessage: `${t('user_usage_banner_text', {
            memberLimit,
            numUsersInOrg,
        })} + ${t('or')} + ${t('user_upgrade_plan_text')} + ${t(
            'user_upgrade_banner_ending',
        )} + '.'`,
        numUsersInOrg,
        owner: isOwner === true,
        userUsageBannerColour: 'red',
    };

    const userAndRowLimitUserActionProperties = {
        dataAndUserUsageBannerMessage: `${t('data_and_user_usage_banner_text', {
            currentUsage: formattedUsage,
            numUsersInOrg,
            rowLimit: formattedRowLimit,
            memberLimit,
        })} + ${t('or')}+ ${t('user_and_data_upgrade_plan_text')} + ${t(
            'user_and_data_upgrade_plan_ending',
        )} + .`,
        dataUsagePercentage: percent,
        numUsersInOrg,
        owner: isOwner === true,
        userAndDataUsageBannerColour: 'red',
    };

    const handleUpgradePlan = (bannerType: BannerType) => {
        if (bannerType === BannerType.data) {
            collectEvent(
                new UserAction(userActionTypeEnum.TEAM_USAGE_BANNER_UPGRADE),
                rowLimitUserActionProperties,
            );
        } else if (bannerType === BannerType.user) {
            collectEvent(
                new UserAction(
                    userActionTypeEnum.TEAM_USER_LIMIT_BANNER_UPGRADE,
                ),
                userLimitUserActionProperties,
            );
        } else if (bannerType === BannerType.userAndData) {
            collectEvent(
                new UserAction(
                    userActionTypeEnum.TEAM_USER_AND_DATA_LIMIT_BANNER_UPGRADE,
                ),
                userAndRowLimitUserActionProperties,
            );
        }
        if (bannerType !== BannerType.data) {
            (window as any).Intercom('show');
        } else {
            if (isOrgsOnFTEnabled() && hasAdminPrivileges()) {
                goToAdmin('pricing');
            } else goToTeamsPage();
            setSourceToPricingPage(`${bannerType} Usage Banner (Global)`);
        }
    };
    const handleReduceRow = () => {
        collectEvent(
            new UserAction(userActionTypeEnum.TEAM_USAGE_BANNER_REDUCE),
            rowLimitUserActionProperties,
        );
    };

    let message;

    const isDataUsageBannerVisible =
        percent && percent >= 100 && showDataUsageBanner;

    const isUserLimitBannerVisble =
        showUserLimitBanner && numUsersInOrg > memberLimit;

    if (isDataUsageBannerVisible && isUserLimitBannerVisble) {
        message = (
            <Typography variant="body-normal" color={Colors.base} noMargin>
                {t('data_and_user_usage_banner_text', {
                    currentUsage: formattedUsage,
                    numUsersInOrg,
                    rowLimit: formattedRowLimit,
                    memberLimit,
                })}
                {t('or')}
                {/* eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-283577] Button-like element with onClick handler only, triggers upgrade flow via Intercom, no href for navigation */}
                <a
                    onClick={() => handleUpgradePlan(BannerType.userAndData)}
                    className={styles.bannerLink}
                    data-testid="DataAndUserUpgradePlanId"
                >
                    {t('user_and_data_upgrade_plan_text')}
                </a>
                {t('user_and_data_upgrade_plan_ending')}.
            </Typography>
        );
    } else if (isDataUsageBannerVisible) {
        message = (
            <Typography variant="body-normal" color={Colors.base} noMargin>
                {t('data_usage_banner_text', { percent })}
                {/* eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-283577] Button-like element with onClick handler only, triggers upgrade flow via Intercom, no href for navigation */}
                <a
                    href={reduceRowDocUrl}
                    onClick={handleReduceRow}
                    data-testid="reduceRowId"
                    target="_blank"
                    rel="noreferrer"
                >
                    {t('reduce_row_text')}
                </a>
                {t('or')}
                {/* eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-283577] Button-like element with onClick handler only, triggers upgrade flow or navigation, no href for navigation */}
                <a
                    onClick={() => handleUpgradePlan(BannerType.data)}
                    className={styles.bannerLink}
                    data-testid="upgradePlanId"
                >
                    {t('upgrade_plan_text')}
                </a>
                .
            </Typography>
        );
    } else if (isUserLimitBannerVisble) {
        message = (
            <Typography variant="body-normal" color={Colors.base} noMargin>
                {t('user_usage_banner_text', { memberLimit, numUsersInOrg })}
                {t('or')}
                {/* eslint-disable-next-line thoughtspot-eslint/restrict-anchor-tags -- [SCAL-283577] Button-like element with onClick handler only, triggers Intercom upgrade flow, no href for navigation */}
                <a
                    onClick={() => handleUpgradePlan(BannerType.user)}
                    className={styles.bannerLink}
                    data-testid="UserUpgradePlanId"
                >
                    {t('user_upgrade_plan_text')}
                </a>
                {t('user_upgrade_banner_ending')}.
            </Typography>
        );
    }

    useEffect(() => {
        if (isDataUsageBannerVisible && !isUserLimitBannerVisble) {
            collectEvent(
                new UserAction(userActionTypeEnum.TEAM_USAGE_BANNER_SEEN),
                rowLimitUserActionProperties,
            );
        } else if (!isDataUsageBannerVisible && isUserLimitBannerVisble) {
            collectEvent(
                new UserAction(userActionTypeEnum.TEAM_USER_USAGE_BANNER_SEEN),
                userLimitUserActionProperties,
            );
        } else if (isDataUsageBannerVisible && isUserLimitBannerVisble) {
            collectEvent(
                new UserAction(
                    userActionTypeEnum.TEAM_USER_AND_ROW_USAGE_BANNER_SEEN,
                ),
                userAndRowLimitUserActionProperties,
            );
        }
    }, []);
    return (
        <BannerAlert
            message={message}
            type={BannerAlertType.ERROR}
            isVisible
            showCloseButton={false}
            global
            data-testid="user-and-data-usage-banner-test-id"
            className={styles.banner}
        />
    );
};
